import React from "react";

import { Box, CircularProgress, Typography } from "@mui/material";
import { useFormatNumericHook } from "hooks";
import type { PerformanceAttributionCardData, PerformanceCardConfig } from "store/types";

interface Props {
  config: PerformanceCardConfig;
  data?: PerformanceAttributionCardData;
  loading?: boolean;
}

const SORT_LABELS: Record<PerformanceCardConfig["sortBy"], string> = {
  irr: "IRR",
  moic: "MOIC",
  dpi: "DPI",
  rvpi: "RVPI"
};

function getBarColor(index: number): string {
  const colors = ["#0F5D5E", "#1A7A7A", "#2A9D8F", "#48B89C", "#76C7A8"];
  return colors[index % colors.length];
}
export const PerformanceAttributionCard: React.FC<Props> = ({ config, data, loading }) => {
  const formatNumeric = useFormatNumericHook();
  // Apply the global number format (decimals) to the displayed metric, keeping its unit.
  const formatPercent = (value: number): string => `${formatNumeric({ value: value * 100, format: {} })}%`;
  const formatMultiple = (value: number): string => `${formatNumeric({ value, format: {} })}x`;

  const deals = data?.portfolio?.deals ?? [];

  return (
    <Box sx={{ p: 2 }}>
      <Typography
        sx={{
          fontSize: 12,
          fontWeight: 600,
          color: "#6B7280",
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          mb: 0.5
        }}
      >
        Performance Attribution
      </Typography>
      <Typography sx={{ fontSize: 10, color: "#9CA3AF", mb: 1.5 }}>
        Top {config.topN} by {SORT_LABELS[config.sortBy]}
      </Typography>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
          <CircularProgress size={20} />
        </Box>
      )}

      {!loading && deals.length === 0 && (
        <Box sx={{ py: 3, textAlign: "center" }}>
          <Typography sx={{ fontSize: 12, color: "#9CA3AF" }}>No deals to display</Typography>
        </Box>
      )}

      {!loading && deals.length > 0 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          {deals.map((deal, index) => {
            const barWidth = 100;
            const displayValue = config.sortBy === "irr" ? formatPercent(deal.value) : formatMultiple(deal.value);

            return (
              <Box key={index}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: "4px" }}>
                  <Typography
                    sx={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: "#243B53",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      maxWidth: "65%"
                    }}
                  >
                    {deal.name}
                  </Typography>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#071f35" }}>{displayValue}</Typography>
                </Box>
                <Box sx={{ position: "relative", height: 8, bgcolor: "#E5E7EB", borderRadius: 4, overflow: "hidden" }}>
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      height: "100%",
                      width: `${barWidth.toFixed(1)}%`,
                      bgcolor: getBarColor(index),
                      borderRadius: 4,
                      transition: "width 0.3s ease"
                    }}
                  />
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};
